import React, { useRef } from 'react';
import { Button, Form } from 'react-bootstrap';

export type ViewMode = 'fit-width' | 'single-page' | 'zoom';

interface ViewerToolbarProps {
  displayZoomPct: number;
  viewMode: ViewMode;
  numPages: number;
  currentPage: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onSetZoomPct: (pct: number) => void;
  onSetViewMode: (mode: ViewMode) => void;
  onNavigatePage: (page: number) => void;
  extra?: React.ReactNode;
}

const ViewerToolbar: React.FC<ViewerToolbarProps> = ({
  displayZoomPct, viewMode, numPages, currentPage,
  onZoomIn, onZoomOut, onSetZoomPct, onSetViewMode, onNavigatePage, extra,
}) => {
  const zoomRevertRef = useRef<boolean>(false);

  return (
    <div className="d-flex align-items-center justify-content-between px-2 py-1 border-bottom bg-body-tertiary" style={{ flexShrink: 0 }}>
      <div className="d-flex align-items-center gap-2">
        {extra && (<>{extra}<div className="vr" /></>)}
        <Button variant="outline-secondary" size="sm" onClick={onZoomOut} title="Zoom out">
          <i className="bi bi-zoom-out"></i>
        </Button>
        <Form.Control
          key={displayZoomPct}
          type="text"
          size="sm"
          style={{ width: '4.5em', textAlign: 'center', padding: '0.15rem 0.25rem' }}
          defaultValue={`${displayZoomPct}%`}
          onFocus={(e) => { e.target.value = String(displayZoomPct); e.target.select(); }}
          onBlur={(e) => {
            if (zoomRevertRef.current) {
              zoomRevertRef.current = false;
              e.target.value = `${displayZoomPct}%`;
              return;
            }
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val > 0) {
              onSetZoomPct(val);
            } else {
              e.target.value = `${displayZoomPct}%`;
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              zoomRevertRef.current = true;
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
        <Button variant="outline-secondary" size="sm" onClick={onZoomIn} title="Zoom in">
          <i className="bi bi-zoom-in"></i>
        </Button>
        <div className="vr" />
        <Button
          variant={viewMode === 'fit-width' ? 'primary' : 'outline-secondary'}
          size="sm"
          onClick={() => onSetViewMode('fit-width')}
          title="Fit page width to viewer"
        >
          <i className="bi bi-arrows"></i> Fit Width
        </Button>
        <Button
          variant={viewMode === 'single-page' ? 'primary' : 'outline-secondary'}
          size="sm"
          onClick={() => onSetViewMode('single-page')}
          title="Fit entire page in viewer"
        >
          <i className="bi bi-file-earmark"></i> Single Page
        </Button>
      </div>
      {numPages > 1 && (
        <div className="d-flex align-items-center gap-2">
          <div className="vr" />
          <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1}
            onClick={() => onNavigatePage(currentPage - 1)}>
            <i className="bi bi-chevron-left"></i>
          </Button>
          <span className="small">{currentPage} / {numPages}</span>
          <Button variant="outline-secondary" size="sm" disabled={currentPage >= numPages}
            onClick={() => onNavigatePage(currentPage + 1)}>
            <i className="bi bi-chevron-right"></i>
          </Button>
        </div>
      )}
      <div className="d-flex align-items-center gap-2">
        {numPages > 0 && <span className="small text-muted">{numPages} page{numPages !== 1 ? 's' : ''}</span>}
      </div>
    </div>
  );
};

export default ViewerToolbar;
